import * as React from "react";
import { useState, useEffect, useRef } from "react";
import {
  Text,
  Spinner,
  Button,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { ArrowLeft24Regular, ChevronLeft24Regular, ChevronRight24Regular } from "@fluentui/react-icons";
import {
  DiscussionDto,
  DiscussionCommentDto,
  getCommentsByDiscussion,
  archiveComment,
  archiveDiscussion,
} from "../../../services/discussions.service";
import { StatusItemDto } from "../../../services/status-items.service";
import { MentionUser } from "./richtext/types";
import { getUserFriendlyErrorMessage } from "../../../utils/error-messages";
import { useWorkflowIntelligence } from "../../../contexts/WorkflowIntelligenceContext";
import DiscussionPost from "./DiscussionPost";
import CommentList from "./CommentList";
import CommentComposer from "./CommentComposer";
import DiscussionAttachments from "./DiscussionAttachments";
import { useFeatureFlags } from "../../../contexts/FeatureFlagContext";
import EditDiscussionDialog from "./EditDiscussionDialog";
import { useTypographyStyles } from "../../../styles/typography.styles";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: tokens.colorNeutralBackground1,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
  },
  navigationContainer: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    marginRight: tokens.spacingHorizontalL,
  },
  content: {
    // Scrollable middle region — takes all available space between the header
    // and the anchored composer footer. DiscussionPost + CommentList +
    // attachments panel live in here.
    flex: 1,
    overflow: "auto",
    padding: tokens.spacingVerticalL,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
  },
  attachmentsPanel: {
    // Pin the panel to the bottom of the scrollable region. `margin-top:
    // auto` on the last flex child soaks up any spare vertical space, so
    // on a short thread the attachments sit just above the composer
    // instead of floating directly under the last comment. When the
    // thread is long enough to overflow there's no spare space to soak
    // up, so this is inert and the panel stays where it already was —
    // at the end of the scrolled content.
    marginTop: "auto",
  },
  composerFooter: {
    // Anchored to the bottom of the thread — doesn't scroll with the content.
    // A top border keeps it visually separated from the scrolling comments
    // above when they run right up to the edge.
    flexShrink: 0,
    padding: tokens.spacingVerticalL,
    paddingTop: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalXXL,
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorPaletteRedForeground1,
  },
});

interface DiscussionThreadProps {
  discussion: DiscussionDto;
  statusItems?: StatusItemDto[];
  onBack: () => void;
  onDiscussionUpdated?: (discussion: DiscussionDto) => void;
  onDiscussionArchived?: (discussionId: number) => void;
  onNavigatePrevious?: () => void;
  onNavigateNext?: () => void;
  currentIndex?: number;
  totalCount?: number;
  users?: MentionUser[];
}

/**
 * Inline thread view for a single discussion — original post, comment list,
 * comment composer, status controls, prev/next navigation, and edit/archive
 * actions. Rendered inside the Discussions page when a card is selected.
 */
const DiscussionThread: React.FC<DiscussionThreadProps> = ({
  discussion: initialDiscussion,
  statusItems,
  onBack,
  onDiscussionUpdated,
  onDiscussionArchived,
  onNavigatePrevious,
  onNavigateNext,
  currentIndex,
  totalCount,
  users,
}) => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const { enableDiscussionAttachments } = useFeatureFlags();
  // File input + selected-files state live here so DiscussionAttachments can
  // stay a plain functional component driven by props. Clicking the composer's
  // Attach button triggers the hidden input; its onChange populates
  // pendingAttachmentFiles, which the attachments panel picks up and uploads,
  // then clears via onFilesConsumed.
  const attachInputRef = useRef<HTMLInputElement>(null);
  const [pendingAttachmentFiles, setPendingAttachmentFiles] = useState<File[]>([]);
  const { userInfo } = useWorkflowIntelligence();
  const currentUserEmail = userInfo?.mail || userInfo?.userPrincipalName;

  const [discussion, setDiscussion] = useState<DiscussionDto>(initialDiscussion);
  const [comments, setComments] = useState<DiscussionCommentDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingComment, setEditingComment] = useState<DiscussionCommentDto | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  // Sync internal state when navigating to a different discussion
  useEffect(() => {
    setDiscussion(initialDiscussion);
    setEditingComment(null);
  }, [initialDiscussion]);

  // Paste-to-attach — a document-level listener so users can Ctrl+V a
  // screenshot without needing to focus a specific control. We only
  // intercept the event when the clipboard actually carries files, so
  // ordinary text paste into the comment box still works as before.
  useEffect(() => {
    if (!enableDiscussionAttachments) return;
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const files: File[] = [];
      for (const item of Array.from(items)) {
        if (item.kind === "file") {
          const f = item.getAsFile();
          if (f) files.push(f);
        }
      }
      if (files.length > 0) {
        e.preventDefault();
        setPendingAttachmentFiles(files);
      }
    };
    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [enableDiscussionAttachments]);

  // Load comments on mount or when discussion changes
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const commentsData = await getCommentsByDiscussion(discussion.id);
        setComments(commentsData);
      } catch (err) {
        const friendlyMessage = getUserFriendlyErrorMessage(err);
        setError(friendlyMessage);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [discussion.id]);

  const handleCommentAdded = (newComment: DiscussionCommentDto) => {
    setComments((prev) => [...prev, newComment]);

    // If the comment includes a status change, update the discussion
    if (newComment.statusAfter) {
      const updatedDiscussion = { ...discussion, status: newComment.statusAfter };
      setDiscussion(updatedDiscussion);
      if (onDiscussionUpdated) {
        onDiscussionUpdated(updatedDiscussion);
      }
    }
  };

  const handleEditComment = (comment: DiscussionCommentDto) => {
    setEditingComment(comment);
  };

  const handleCommentUpdated = (updatedComment: DiscussionCommentDto) => {
    setComments((prev) =>
      prev.map((c) => (c.id === updatedComment.id ? updatedComment : c))
    );
    setEditingComment(null);
  };

  const handleCancelEdit = () => {
    setEditingComment(null);
  };

  const handleArchiveComment = async (comment: DiscussionCommentDto) => {
    try {
      const archivedComment = await archiveComment(discussion.id, comment.id);
      setComments((prev) =>
        prev.map((c) => (c.id === archivedComment.id ? archivedComment : c))
      );
    } catch (err) {
      console.error("Failed to archive comment:", err);
    }
  };

  // Discussion edit handlers
  const handleEditDiscussion = () => {
    setIsEditDialogOpen(true);
  };

  const handleEditDialogDismiss = () => {
    setIsEditDialogOpen(false);
  };

  const handleDiscussionEditSuccess = (updatedDiscussion: DiscussionDto) => {
    setDiscussion(updatedDiscussion);
    setIsEditDialogOpen(false);
    if (onDiscussionUpdated) {
      onDiscussionUpdated(updatedDiscussion);
    }
  };

  const handleArchiveDiscussion = async () => {
    try {
      await archiveDiscussion(discussion.id);
      if (onDiscussionArchived) {
        onDiscussionArchived(discussion.id);
      }
      onBack();
    } catch (err) {
      console.error("Failed to archive discussion:", err);
    }
  };

  // Determine if current user can edit/archive the discussion. Wrap in
  // Boolean(...) so the chained && expressions stay typed as boolean rather
  // than string | boolean (currentUserEmail short-circuits to its string value).
  const isDiscussionOwner = Boolean(
    currentUserEmail &&
    discussion.createdByEmail.toLowerCase() === currentUserEmail.toLowerCase()
  );
  const canEditDiscussion = isDiscussionOwner && !discussion.isArchived;
  const canArchiveDiscussion = isDiscussionOwner && !discussion.isArchived;

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Button
            appearance="subtle"
            icon={<ArrowLeft24Regular />}
            onClick={onBack}
          >
            Back
          </Button>
        </div>
        <div className={styles.loadingContainer}>
          <Spinner size="large" />
          <Text>Loading discussion...</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Button
            appearance="subtle"
            icon={<ArrowLeft24Regular />}
            onClick={onBack}
          >
            Back
          </Button>
        </div>
        <div className={styles.errorContainer}>
          <Text className={typography.pageTitle}>
            Error loading discussion
          </Text>
          <Text>{error}</Text>
          <Button appearance="primary" onClick={onBack}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const showNavigation = totalCount !== undefined && totalCount > 1 && currentIndex !== undefined;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Button
            appearance="subtle"
            icon={<ArrowLeft24Regular />}
            onClick={onBack}
          >
            Back
          </Button>
          <Text className={typography.pageTitle}>
            {discussion.title}
          </Text>
        </div>
        {showNavigation && (
          <div className={styles.navigationContainer}>
            <Button
              appearance="subtle"
              icon={<ChevronLeft24Regular />}
              disabled={currentIndex === 0}
              onClick={onNavigatePrevious}
            />
            <Text className={typography.bodyLarge}>
              {currentIndex + 1} of {totalCount}
            </Text>
            <Button
              appearance="subtle"
              icon={<ChevronRight24Regular />}
              disabled={currentIndex === totalCount - 1}
              onClick={onNavigateNext}
            />
          </div>
        )}
      </div>

      <div className={styles.content}>
        <DiscussionPost
          discussion={discussion}
          statusItems={statusItems}
          isEditable={canEditDiscussion}
          isArchivable={canArchiveDiscussion}
          onEditClick={handleEditDiscussion}
          onArchiveClick={handleArchiveDiscussion}
        />

        <CommentList
          comments={comments}
          currentUserEmail={currentUserEmail}
          onEditComment={handleEditComment}
          onArchiveComment={handleArchiveComment}
        />

        {enableDiscussionAttachments && (
          <DiscussionAttachments
            className={styles.attachmentsPanel}
            discussionId={discussion.id}
            pendingFiles={pendingAttachmentFiles}
            onFilesConsumed={() => setPendingAttachmentFiles([])}
          />
        )}
      </div>

      <div className={styles.composerFooter}>
        {/* File input owned here (not in DiscussionAttachments) so the panel
            can stay hidden until it has data, while the Attach button in
            CommentComposer still opens a picker. Gated on the same feature
            flag as the attachments panel so nothing attachment-related
            renders when the feature is off. */}
        {enableDiscussionAttachments && (
          <input
            ref={attachInputRef}
            type="file"
            multiple
            accept="image/png,image/jpeg,image/gif,image/webp"
            style={{ display: "none" }}
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              if (files.length > 0) setPendingAttachmentFiles(files);
              // Reset value so choosing the same file twice re-fires onChange.
              if (attachInputRef.current) attachInputRef.current.value = "";
            }}
          />
        )}

        <CommentComposer
          onAttachClick={enableDiscussionAttachments ? (() => attachInputRef.current?.click()) : undefined}
          discussionId={discussion.id}
          discussionType={discussion.type}
          currentStatus={discussion.status}
          onCommentAdded={handleCommentAdded}
          editingComment={editingComment}
          onCommentUpdated={handleCommentUpdated}
          onCancelEdit={handleCancelEdit}
        />
      </div>

      <EditDiscussionDialog
        isOpen={isEditDialogOpen}
        onDismiss={handleEditDialogDismiss}
        discussion={discussion}
        users={users}
        onSuccess={handleDiscussionEditSuccess}
      />
    </div>
  );
};

export default DiscussionThread;
